/**
 * B2BUserContext — Phase 33 + Phase 35 + Phase 74 (Agent Multi-Company)
 *
 * ─── Phase 74: Multi-Company Agent Switching ───────────────────────────────
 *
 * Independent agents often represent multiple suppliers/companies
 * simultaneously.  The session now exposes:
 *
 *   • availableCompanies  — the distinct (company_id, company_name) pairs
 *                           returned by the get_agent_companies_for_jwt()
 *                           SECURITY DEFINER RPC (Phase 74 SQL).
 *   • activeCompanyId     — the company the agent is currently acting under.
 *                           Defaults to the first available company at login.
 *   • setActiveCompany()  — mutator that:
 *       1. Updates activeCompanyId.
 *       2. Clears the active client (so the next pick is intra-company).
 *       3. Narrows allowedCompanyIds to [activeCompanyId] until a client is
 *          chosen — preventing cross-company catalog/orders leakage.
 *
 * Cascading effect: the existing BranchSwitcher already filters
 * agentClientRecords by activeCompanyId in B2BLayout.tsx (Phase 74 UI), so
 * switching the company immediately rescopes the client picker, the catalog,
 * the cart context and all downstream queries (Catalog, Orders, LIFO Returns).
 *
 * Phase 35 (unchanged): "Order on Behalf"
 * Phase 33/34 (unchanged): client branch switcher
 *
 * Persistence: intentionally NOT persisted — re-seeded on every login.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { B2bClientRecord } from '@/lib/b2bMarketplaceService';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A single row from inventory_agents (the agent's own profile). */
export interface InventoryAgent {
  id:                         string;
  company_id:                 string;
  name:                       string;
  email:                      string | null;
  phone:                      string | null;
  sales_commission_rate:      number | null;
  collection_commission_rate: number | null;
  is_master_agent:            boolean;
  default_warehouse_id:       string | null;
}

/** A slimmed-down client row used in the agent's picker list and branch switcher. */
export interface AgentClientRecord {
  id:           string;
  company_id:   string;
  name:         string;
  email:        string | null;
  phone:        string | null;
  pricelist_id: string | null;
  /** Phase 34: business registration number — used to group branches in switcher */
  business_id?: string | null;
  is_vat_liable?: boolean;
  /** Phase 109: 'none' = agent-only, 'view_only' = browse only, 'full' = can order */
  client_access?: string;
  /** Phase 115: true if the client is directly assigned to this agent (agent_id match).
   *  false if visible only because the agent is a master agent. */
  is_own_client?: boolean;
  /** Phase 115: the agent_id on inventory_clients (the client's assigned agent). */
  assigned_agent_id?: string | null;
  assigned_agent_name?: string | null;
  /** Phase 132: the client's consignment warehouse */
  consignment_warehouse_id?: string | null;
}

/**
 * Phase 74 — a (company_id, company_name) pair the agent is registered under.
 * Returned by the get_agent_companies_for_jwt() RPC.
 */
export interface AgentCompanyOption {
  company_id:   string;
  company_name: string;
}

export type B2BRole = 'client' | 'agent';

export interface B2BUserSession {
  /** 'client' or 'agent' */
  role: B2BRole;

  /** Phase 122: company IDs where this agent has is_master_agent = true.
   *  Empty for 'client' role. Used to derive per-company master status. */
  masterAgentCompanyIds?: string[];

  /**
   * For role='client': inventory_clients.id of the first record at login.
   * For role='agent' : inventory_agents.id.
   */
  profileId: string;

  /**
   * For role='agent': the full inventory_agents row.
   * For role='client': null.
   */
  agentRecord: InventoryAgent | null;

  /**
   * The inventory_clients.id that orders should be placed against.
   * For 'client': equals activeBranchId (kept in sync).
   * For 'agent' : null until the agent picks a client.
   */
  activeClientId: string | null;

  /**
   * The full client record for the currently active branch / client.
   * For 'client': the branch the user is currently shopping as.
   * For 'agent' : the client the agent is acting on behalf of.
   */
  activeClientRecord: AgentClientRecord | null;

  /**
   * For 'client': ALL inventory_clients rows matching this user's email/phone.
   *   — May span multiple suppliers (companies) and/or multiple branches per
   *     supplier.  The branch switcher lists these rows grouped by supplier.
   * For 'agent' : empty array (agents use agentClientRecords instead).
   */
  clientRecords: B2bClientRecord[];

  /**
   * Phase 35 — Agent only.
   * ALL inventory_clients rows where agent_id = agentRecord.id (across every
   * supplier the agent is registered under — Phase 40 multi-supplier).
   * Phase 74: this remains the FULL list; the BranchSwitcher in B2BLayout
   * filters it down to records matching `activeCompanyId` so the agent only
   * sees clients of the currently active supplier.
   * Empty for 'client' role.
   */
  agentClientRecords: AgentClientRecord[];

  /**
   * Allowed company IDs for the CURRENTLY ACTIVE business group / client.
   * For 'client': distinct company_ids of clientRecords sharing the same
   *   business_id as the active branch (updated by setActiveBranch).
   * For 'agent' : [activeClientRecord.company_id] once a client is selected,
   *   or [activeCompanyId] before selection (Phase 74 — used to be the
   *   full multi-supplier list, now narrowed by the company switcher).
   *
   * The catalog fetches `inventory_products WHERE company_id IN allowedCompanyIds`.
   */
  allowedCompanyIds: string[];

  /**
   * company_id → pricelist_id lookup for price resolution.
   * For 'client': from B2bClientSession.pricelistByCompany (all companies).
   * For 'agent' : updated when a client is selected via setActiveBranch.
   */
  pricelistByCompany: Record<string, string | null>;

  // ── Phase 74 ─────────────────────────────────────────────────────────────
  /**
   * Distinct (company_id, company_name) pairs the agent is registered under,
   * sourced from the get_agent_companies_for_jwt() RPC at login / restore.
   * Empty for 'client' role.  When length > 1 the B2B header renders the
   * "בחר ספק / חברה" supplier switcher.
   */
  availableCompanies: AgentCompanyOption[];

  /**
   * The company_id the agent is currently acting under.
   * For 'agent' : initialised to availableCompanies[0]?.company_id (or the
   *   primary agentRecord.company_id when the RPC returns 0 rows). The
   *   BranchSwitcher / catalog / cart all cascade from this value.
   * For 'client': always null — clients always operate per-branch via
   *   activeClientRecord.company_id.
   */
  activeCompanyId: string | null;
}

// ─── Context shape ────────────────────────────────────────────────────────────

interface B2BUserContextType {
  /** null = not yet seeded (loading or not logged in) */
  session: B2BUserSession | null;

  /**
   * Called by B2BLoginPage immediately after OTP/password classification.
   * Populates the context so that the subsequent navigate() renders
   * the correct view without needing to re-fetch the profile.
   */
  seedB2BUser: (session: B2BUserSession) => void;

  /**
   * Phase 33/35: Switch the active branch for BOTH roles.
   *
   * For 'client': groups records by business_id, recomputes allowedCompanyIds.
   * For 'agent' : sets the selected client, sets allowedCompanyIds = [company_id]
   *               and ALSO syncs activeCompanyId to the client's company_id
   *               (Phase 74 — keeps the supplier switcher consistent with the
   *               cascaded selection).
   */
  setActiveBranch: (record: AgentClientRecord) => void;

  /**
   * Legacy alias for setActiveBranch — kept for backward compatibility with
   * B2BClientSelection which uses this name.
   */
  setActiveClientId: (clientRecord: AgentClientRecord) => void;

  /**
   * Called when the agent wants to switch clients (back to selection screen).
   * Resets activeClientId / activeClientRecord to null.
   * Phase 74: allowedCompanyIds is narrowed to [activeCompanyId] so the
   * client picker / catalog stay scoped to the currently selected supplier
   * even before another client is chosen.
   */
  clearActiveClient: () => void;

  /**
   * Phase 74 — switch the active company (supplier) for an agent.
   *
   * Atomically:
   *   1. Sets activeCompanyId to the new company_id.
   *   2. Clears activeClientId / activeClientRecord.
   *   3. Narrows allowedCompanyIds to [companyId] so the catalog and any
   *      already-rendered child page rescopes immediately.
   *
   * No-op for 'client' role or when companyId is not in availableCompanies.
   * Does NOT clear the cart — that is a UX concern handled by the caller in
   * B2BLayout (which also calls clearCart() + navigate('/b2b/agent') so the
   * agent is sent back to the client picker right after switching companies).
   */
  setActiveCompany: (companyId: string) => void;

  /**
   * Phase 74.1 (robustness) — patch the agent's availableCompanies list
   * without replacing the entire session object.
   *
   * Used by the aggressive RPC-fetch effect in B2BLayout that runs
   * regardless of whether the initial seed already populated the list.
   * If the previous activeCompanyId is no longer present in the new list,
   * activeCompanyId falls back to the first available company; if the
   * agent has > 1 company and no client is selected we ALSO narrow
   * allowedCompanyIds to [activeCompanyId] so the catalog never leaks
   * across suppliers between hydration and the first explicit switch.
   *
   * No-op for 'client' role.
   */
  setAgentCompanies: (companies: AgentCompanyOption[]) => void;
}

// ─── Context + Provider ───────────────────────────────────────────────────────

const B2BUserContext = createContext<B2BUserContextType | undefined>(undefined);

export function B2BUserProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<B2BUserSession | null>(null);

  const seedB2BUser = useCallback((s: B2BUserSession) => {
    setSession(s);
  }, []);

  // ── setActiveBranch — unified for client + agent ───────────────────────────
  const setActiveBranch = useCallback((record: AgentClientRecord) => {
    setSession(prev => {
      if (!prev) return prev;

      if (prev.role === 'client') {
        // Scope to the selected record's supplier only — no cross-supplier
        // grouping by business_id.  The client can switch suppliers via the
        // supplier switcher (setActiveCompany), not via branch switching.
        return {
          ...prev,
          activeClientId:     record.id,
          activeClientRecord: record,
          activeCompanyId:    record.company_id,
          allowedCompanyIds:  [record.company_id],
          pricelistByCompany: {
            ...prev.pricelistByCompany,
            [record.company_id]: record.pricelist_id,
          },
        };
      }

      if (prev.role === 'agent') {
        // Agent selects a client: scope catalog to that client's company.
        // Phase 74: sync activeCompanyId so the supplier switcher reflects
        // the cascaded selection (e.g. when the client was picked from a
        // search rather than after explicitly switching the supplier).
        return {
          ...prev,
          activeClientId:     record.id,
          activeClientRecord: record,
          allowedCompanyIds:  [record.company_id],
          activeCompanyId:    record.company_id,
          pricelistByCompany: {
            ...prev.pricelistByCompany,
            [record.company_id]: record.pricelist_id,
          },
        };
      }

      return prev;
    });
  }, []);

  // ── setActiveClientId — legacy alias used by B2BClientSelection ───────────
  const setActiveClientId = useCallback((clientRecord: AgentClientRecord) => {
    setActiveBranch(clientRecord);
  }, [setActiveBranch]);

  const clearActiveClient = useCallback(() => {
    setSession(prev => {
      if (!prev) return prev;
      // Phase 74: narrow allowedCompanyIds to [activeCompanyId] so the agent
      // never sees products / clients from another supplier between client
      // selections.  Falls back to the agentRecord.company_id, then to the
      // first availableCompanies entry, and finally to the existing list as
      // a last resort to remain backwards compatible.
      let restoredCompanyIds = prev.allowedCompanyIds;
      if (prev.role === 'agent') {
        const fallback =
          prev.activeCompanyId
          ?? prev.agentRecord?.company_id
          ?? prev.availableCompanies[0]?.company_id
          ?? null;
        restoredCompanyIds = fallback ? [fallback] : prev.allowedCompanyIds;
      }
      return {
        ...prev,
        activeClientId:     null,
        activeClientRecord: null,
        allowedCompanyIds:  restoredCompanyIds,
      };
    });
  }, []);

  // ── Phase 74: setActiveCompany — switch the active supplier ──────────────
  const setActiveCompany = useCallback((companyId: string) => {
    setSession(prev => {
      if (!prev) return prev;
      // Defensive guard: only allow switching to a company in availableCompanies.
      const known = prev.availableCompanies.some(c => c.company_id === companyId);
      if (!known) return prev;

      if (prev.role === 'agent') {
        // No-op when the same company is already active and there is no
        // active client to clear — avoids needless re-renders.
        if (
          prev.activeCompanyId === companyId
          && prev.activeClientId === null
          && prev.allowedCompanyIds.length === 1
          && prev.allowedCompanyIds[0] === companyId
        ) {
          return prev;
        }
        const isMasterForCompany = (prev.masterAgentCompanyIds ?? []).includes(companyId);
        return {
          ...prev,
          activeCompanyId:    companyId,
          activeClientId:     null,
          activeClientRecord: null,
          allowedCompanyIds:  [companyId],
          agentRecord:        prev.agentRecord ? {
            ...prev.agentRecord,
            is_master_agent: isMasterForCompany,
          } : null,
        };
      }

      if (prev.role === 'client') {
        // Find the client's record for this supplier
        const record = prev.clientRecords.find(
          r => r.company_id === companyId,
        );
        if (!record) return prev;
        return {
          ...prev,
          activeCompanyId:    companyId,
          activeClientId:     record.id,
          activeClientRecord: {
            id:           record.id,
            company_id:   record.company_id,
            name:         record.name,
            email:        record.email,
            phone:        record.phone,
            pricelist_id: record.pricelist_id,
            business_id:  record.business_id ?? null,
          },
          allowedCompanyIds: [companyId],
        };
      }

      return prev;
    });
  }, []);

  // ── Phase 74.1: setAgentCompanies — patch availableCompanies in-place ─────
  // Used by the aggressive RPC-fetch effect in B2BLayout that runs after the
  // initial seed.  Tolerates schema-cache races and empty results without
  // destroying the existing session or active client selection.
  const setAgentCompanies = useCallback((companies: AgentCompanyOption[]) => {
    setSession(prev => {
      if (!prev) return prev;
      if (prev.role !== 'agent') return prev;

      // Defensive de-dup by company_id (RPC should already DISTINCT, but the
      // fallback derived list might not).
      const seen = new Set<string>();
      const deduped: AgentCompanyOption[] = [];
      for (const c of companies) {
        if (!c?.company_id || seen.has(c.company_id)) continue;
        seen.add(c.company_id);
        deduped.push({
          company_id:   c.company_id,
          company_name: c.company_name ?? '',
        });
      }

      // Decide the next activeCompanyId:
      //   1. Keep the previous one if it's still in the new list.
      //   2. Otherwise fall back to the first entry, then to agentRecord, then null.
      const previousStillValid =
        prev.activeCompanyId !== null
        && deduped.some(c => c.company_id === prev.activeCompanyId);
      const nextActiveCompanyId = previousStillValid
        ? prev.activeCompanyId
        : (deduped[0]?.company_id ?? prev.agentRecord?.company_id ?? null);

      // Narrow allowedCompanyIds to [nextActiveCompanyId] only when no client
      // is currently active — once a client is picked the active company is
      // already pinned by setActiveBranch and we should not interfere.
      let nextAllowedCompanyIds = prev.allowedCompanyIds;
      if (prev.activeClientId === null && nextActiveCompanyId) {
        nextAllowedCompanyIds = [nextActiveCompanyId];
      }

      return {
        ...prev,
        availableCompanies: deduped,
        activeCompanyId:    nextActiveCompanyId,
        allowedCompanyIds:  nextAllowedCompanyIds,
      };
    });
  }, []);

  return (
    <B2BUserContext.Provider value={{
      session,
      seedB2BUser,
      setActiveBranch,
      setActiveClientId,
      clearActiveClient,
      setActiveCompany,
      setAgentCompanies,
    }}>
      {children}
    </B2BUserContext.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useB2BUser(): B2BUserContextType {
  const ctx = useContext(B2BUserContext);
  if (!ctx) throw new Error('useB2BUser must be used inside B2BUserProvider');
  return ctx;
}

/**
 * Shorthand: returns the inventory_clients.id that should be used when
 * submitting orders.  Returns null when the session is not yet seeded or
 * when the user is an agent who hasn't picked a client yet.
 */
export function useActiveClientId(): string | null {
  const { session } = useB2BUser();
  return session?.activeClientId ?? null;
}
